export const getDynamicSchema = (campaignSchema, bexioData, wsProvider) => {
    const dynamicSchema = { ...campaignSchema };
    
    if (bexioData) {
        // Filter companies that match the selected client
        if (bexioData.companies) {
            let filteredCompanies = [];
            let otherCompanies = [];
            
            if (wsProvider.clientSelected) {
                // Split companies into filtered and others
                bexioData.companies.forEach(company => {
                    if (company.primary_client_id === wsProvider.clientSelected) {
                        filteredCompanies.push(company);
                    } else {
                        otherCompanies.push(company);
                    }
                });
            } else {
                // If no client selected, show all companies
                filteredCompanies = bexioData.companies;
            }
            
            // Sort companies by primary_client_id first, then by company ID
            const getCompanySortKey = (company) => {
                const clientId = company.primary_client_id || '';
                const companyId = company.nr || '';
                return `${clientId} - ${companyId}`;
            };
            
            filteredCompanies.sort((a, b) => getCompanySortKey(a).localeCompare(getCompanySortKey(b)));
            otherCompanies.sort((a, b) => getCompanySortKey(a).localeCompare(getCompanySortKey(b)));
            
            // Combine filtered companies first, then others with separator
            const allCompanies = [...filteredCompanies];
            if (otherCompanies.length > 0) {
                // Add separator
                allCompanies.push({ nr: '---', full_name: '--- Other Companies ---' });
                allCompanies.push(...otherCompanies);
            }
            
            dynamicSchema.properties.bexio_company_nr.enum = allCompanies.map(company => company.nr);
            dynamicSchema.properties.bexio_company_nr.enumNames = allCompanies.map(company => 
                company.nr === '---' ? company.full_name : `${company.full_name} - ${company.nr}`
            );
            
            // Add description if no matches found
            if (wsProvider.clientSelected && filteredCompanies.length === 0) {
                dynamicSchema.properties.bexio_company_nr.description = "No companies found for the selected client";
            }
        }
        
        // Filter persons that match the selected client
        if (bexioData.persons) {
            let filteredPersons = [];
            let otherPersons = [];
            
            if (wsProvider.clientSelected) {
                // Split persons into filtered and others
                bexioData.persons.forEach(person => {
                    if (person.primary_client_ids && person.primary_client_ids.includes(wsProvider.clientSelected)) {
                        filteredPersons.push(person);
                    } else {
                        otherPersons.push(person);
                    }
                });
            } else {
                // If no client selected, show all persons
                filteredPersons = bexioData.persons;
            }
            
            // Sort persons alphabetically by display name and primary client ID
            const getPersonDisplayName = (person) => {
                let displayName = person.full_name;
                if (!displayName || displayName.trim() === '') {
                    const firstName = person.firstname || '';
                    const lastName = person.lastname || '';
                    displayName = `${firstName} ${lastName}`.trim();
                }
                if (!displayName || displayName.trim() === '') {
                    displayName = person.mail || 'Unknown';
                }
                return displayName;
            };
            
            const getPersonSortKey = (person) => {
                const displayName = getPersonDisplayName(person);
                const primaryClientId = person.primary_client_ids && person.primary_client_ids.length > 0 
                    ? person.primary_client_ids[0] 
                    : '';
                return `${primaryClientId} - ${displayName}`;
            };
            
            filteredPersons.sort((a, b) => getPersonSortKey(a).localeCompare(getPersonSortKey(b)));
            otherPersons.sort((a, b) => getPersonSortKey(a).localeCompare(getPersonSortKey(b)));
            
            // Combine filtered persons first, then others with separator
            const allPersons = [...filteredPersons];
            if (otherPersons.length > 0) {
                // Add separator
                allPersons.push({ nr: '---', full_name: '--- Other Persons ---' });
                allPersons.push(...otherPersons);
            }
            
            dynamicSchema.properties.bexio_recipients.items.properties.bexio_human_nr.enum = allPersons.map(person => person.nr);
            dynamicSchema.properties.bexio_recipients.items.properties.bexio_human_nr.enumNames = allPersons.map(person => {
                if (person.nr === '---') {
                    return person.full_name;
                }
                
                // Try to get the full name, fallback to firstname + lastname if full_name is empty
                let displayName = getPersonDisplayName(person);
                
                // Check if this person is in the "other" section (not filtered for current client)
                const isOtherPerson = wsProvider.clientSelected && 
                    (!person.primary_client_ids || !person.primary_client_ids.includes(wsProvider.clientSelected));
                
                if (isOtherPerson && person.primary_client_ids && person.primary_client_ids.length > 0) {
                    // Show primary client ID in front for other persons
                    const primaryClientId = person.primary_client_ids[0]; // Take the first primary client
                    return `${primaryClientId} - ${displayName} - ${person.nr}`;
                } else {
                    return `${displayName} - ${person.nr}`;
                }
            });
            
            // Add description if no matches found
            if (wsProvider.clientSelected && filteredPersons.length === 0) {
                dynamicSchema.properties.bexio_recipients.items.properties.bexio_human_nr.description = "No persons found for the selected client";
            }
        }
    }
    
    return dynamicSchema;
};

export const getUiSchema = (testCampaignFilterQuery) => ({
    "ui:order": [
        "client_id",
        "campaign_name",
        "enable_invoicing",
        "include_person_in_invoice_header",
        "bexio_company_nr",
        "bexio_recipients",
        "campaign_start_date",
        "campaign_end_date",
        "campaign_budget",
        "campaign_display_groups",
        "*"
    ],
    client_id: {
        "ui:readonly": true
    },
    include_person_in_invoice_header: {
        "ui:widget": "checkbox"
    },
    bexio_company_nr: {
        "ui:widget": "select"
    },
    bexio_recipients: {
        items: {
            bexio_human_nr: {
                "ui:widget": "select"
            }
        }
    },
    campaign_start_date: {
        "ui:widget": "date-time"
    },
    campaign_end_date: {
        "ui:widget": "date-time",
    },
    campaign_display_groups: {
        items: {
            filter: {
                "ui:widget": "monacoes",
                "ui:options": {
                    onSearch: (value, campaign, callback) => { return testCampaignFilterQuery(value, campaign, callback) }
                }
            }
        }
    }
}); 